import type { FormikHelpers } from "formik";
import { useCallback, useMemo } from "react";

import ActionForm from "metabase/actions/components/ActionForm";
import type {
  DashboardId,
  OnSubmitActionForm,
  Parameter,
  ParameterId,
  ParametersForActionExecution,
  WritebackAction,
  WritebackParameter,
} from "metabase-types/api";

export interface ActionParametersInputFormProps {
  action: WritebackAction;
  mappedParameters?: WritebackParameter[];
  initialValues?: ParametersForActionExecution;
  dashboardId?: DashboardId;
  dashboardParameters?: Record<ParameterId, Parameter>;
  allDashboardParameters?: Parameter[];
  onSubmit: OnSubmitActionForm;
  onSubmitSuccess?: (
    actions: FormikHelpers<ParametersForActionExecution>,
  ) => void;
  onCancel?: () => void;
}

function ActionParametersInputForm({
  action,
  mappedParameters = [],
  initialValues = {},
  dashboardId,
  dashboardParameters,
  allDashboardParameters,
  onCancel,
  onSubmit,
  onSubmitSuccess,
}: ActionParametersInputFormProps) {
  const hiddenFields = useMemo(() => {
    return Object.values(action.visualization_settings?.fields ?? {})
      .filter((field) => field.hidden)
      .map((field) => field.id);
  }, [action.visualization_settings?.fields]);

  const handleSubmit = useCallback(
    async (
      parameters: ParametersForActionExecution,
      actions: FormikHelpers<ParametersForActionExecution>,
    ) => {
      actions.setSubmitting(true);
      const { success, error } = await onSubmit(parameters);
      if (success) {
        actions.setErrors({});
        onSubmitSuccess?.(actions);
      } else {
        throw error;
      }
    },
    [onSubmit, onSubmitSuccess],
  );

  return (
    <ActionForm
      action={action}
      initialValues={initialValues}
      hiddenFields={hiddenFields}
      dashboardId={dashboardId}
      dashboardParameters={dashboardParameters}
      allDashboardParameters={allDashboardParameters}
      onSubmit={handleSubmit}
      onClose={onCancel}
    />
  );
}

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default ActionParametersInputForm;
